/**
 * useEarlyAdopters - Early Adopter tier availability.
 *
 * get_early_adopter_status returns the REAL numbers (50 slots in total,
 * claimed = active slots). A slot is taken by the server only when a payment
 * succeeds — the browser never claims one. When the counter is unknown
 * (loading / error) the offer is hidden rather than shown with made-up numbers.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface EarlyAdopterStatus {
  total_slots: number;
  claimed_slots: number;
  remaining_slots: number;
  percentage_claimed: number;
  is_available: boolean;
}

interface ClaimResult {
  success: boolean;
  slot_number: number;
  message: string;
}

/**
 * Get current early adopter slots status (null = unknown).
 */
export function useEarlyAdopterStatus() {
  return useQuery({
    queryKey: ['early-adopter-status'],
    queryFn: async (): Promise<EarlyAdopterStatus | null> => {
      const { data, error } = await supabase.rpc('get_early_adopter_status');

      if (error) {
        console.error('Error fetching early adopter status:', error);
        return null;
      }

      return (data?.[0] as EarlyAdopterStatus | undefined) ?? null;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/**
 * Check if a user is an early adopter
 */
export function useIsEarlyAdopter(userId: string | null) {
  return useQuery({
    queryKey: ['is-early-adopter', userId],
    queryFn: async (): Promise<boolean> => {
      if (!userId) return false;

      const { data, error } = await supabase.rpc('is_early_adopter', {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error checking early adopter status:', error);
        return false;
      }

      return data ?? false;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * @deprecated Slots are claimed by the server when the payment succeeds
 * (claim_early_adopter_slot is service-role only). Kept so old imports
 * compile; it always fails with a clear message.
 */
export function useClaimEarlyAdopterSlot() {
  return useMutation({
    mutationFn: async (_userId: string): Promise<ClaimResult> => {
      throw new Error('Early Adopter slots are assigned automatically after payment.');
    },
  });
}

export interface EarlyAdopterProgress {
  /** The counter was loaded. */
  known: boolean;
  /** Early Adopter can be bought right now. */
  isAvailable: boolean;
  percentage: number;
  remaining: number;
  total: number;
  isSoldOut: boolean;
  isAlmostGone: boolean; // 5 or fewer left
  urgencyLevel: 'low' | 'medium' | 'high' | 'sold_out';
}

/**
 * Numbers for the progress bar. Unknown status → treated as not available
 * (hidden), never as "30 left".
 */
export function getEarlyAdopterProgress(status: EarlyAdopterStatus | null | undefined): EarlyAdopterProgress {
  if (!status) {
    return {
      known: false,
      isAvailable: false,
      percentage: 0,
      remaining: 0,
      total: 50,
      isSoldOut: true,
      isAlmostGone: false,
      urgencyLevel: 'sold_out',
    };
  }

  const { claimed_slots, remaining_slots, total_slots, is_available } = status;
  const remaining = Math.max(0, remaining_slots);
  const percentage = total_slots > 0 ? Math.min(100, Math.round((claimed_slots / total_slots) * 100)) : 100;
  const available = is_available && remaining > 0;

  let urgencyLevel: EarlyAdopterProgress['urgencyLevel'] = 'low';
  if (!available) {
    urgencyLevel = 'sold_out';
  } else if (remaining <= 5) {
    urgencyLevel = 'high';
  } else if (remaining <= 15) {
    urgencyLevel = 'medium';
  }

  return {
    known: true,
    isAvailable: available,
    percentage,
    remaining,
    total: total_slots,
    isSoldOut: !available,
    isAlmostGone: available && remaining <= 5,
    urgencyLevel,
  };
}

// Backward compatibility exports (alias to old names)
export const useFoundersStatus = useEarlyAdopterStatus;
export const useIsFounder = useIsEarlyAdopter;
export const useClaimFoundersSlot = useClaimEarlyAdopterSlot;
export const getFoundersProgress = getEarlyAdopterProgress;

export default useEarlyAdopterStatus;
